import { useMemo, useState } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { MAX_PACKAGES } from '../../data/conjointData';
import { PackageCard } from './PackageCard';
import { ComparisonTable } from './ComparisonTable';
import { CombinedMarketCapture } from './CombinedMarketCapture';
import { PortfolioPnLSummary } from './PortfolioPnLSummary';
import { SuggestTiersButton } from './SuggestTiersButton';
import { ScenarioManager } from './ScenarioManager';
import { MobilePackageSelector } from './MobilePackageSelector';
import { RecommendedConfig } from '../summary/RecommendedConfig';
import { InsightCard } from '../ai/InsightCard';
import { Plus } from 'lucide-react';

export function PackageBuilderTab() {
  const packages = useSimulatorStore(s => s.packages);
  const addPackage = useSimulatorStore(s => s.addPackage);
  const portfolio = usePortfolioPnL();
  const [demandMode, setDemandMode] = useState<'ceiling' | 'split'>('split');

  const bestIdx = useMemo(() => {
    if (portfolio.packages.length === 0) return -1;
    let best = 0;
    const contributions = portfolio.packages;
    for (let i = 1; i < contributions.length; i++) {
      const current = demandMode === 'ceiling' ? contributions[i].contributionMarginCeiling : contributions[i].contributionMarginSplit;
      const bestVal = demandMode === 'ceiling' ? contributions[best].contributionMarginCeiling : contributions[best].contributionMarginSplit;
      if (current > bestVal) best = i;
    }
    const bestMargin = demandMode === 'ceiling' ? contributions[best].contributionMarginCeiling : contributions[best].contributionMarginSplit;
    return bestMargin > 0 ? best : -1;
  }, [portfolio, demandMode]);

  const toggleDemandMode = () => setDemandMode(m => m === 'ceiling' ? 'split' : 'ceiling');

  const gridCols = packages.length <= 2
    ? 'grid-cols-1 lg:grid-cols-2'
    : packages.length <= 3
      ? 'grid-cols-1 lg:grid-cols-3'
      : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

  return (
    <div>
      <InsightCard tabId="builder" />

      <div className="mb-6">
        <RecommendedConfig />
      </div>

      <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <SuggestTiersButton />
        <ScenarioManager />
      </div>

      {/* Desktop: dynamic grid */}
      <div className={`hidden md:grid ${gridCols} gap-6 mb-6`}>
        {packages.map((pkg, i) => (
          <PackageCard key={`${pkg.name}-${i}`} index={i} config={pkg} contribution={portfolio.packages[i]} demandMode={demandMode} isRecommended={i === bestIdx} />
        ))}
        {packages.length < MAX_PACKAGES && (
          <button
            onClick={addPackage}
            className="card border-2 border-dashed border-border/60 hover:border-chart-teal/40 flex flex-col items-center justify-center gap-3 min-h-[300px] transition-colors group cursor-pointer"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-border/60 group-hover:border-chart-teal/40 group-hover:bg-chart-teal/5 transition-colors">
              <Plus size={24} className="text-muted group-hover:text-chart-teal transition-colors" />
            </div>
            <span className="text-sm text-muted group-hover:text-chart-teal transition-colors font-medium">Add Package</span>
            <span className="text-micro text-muted">{packages.length} of {MAX_PACKAGES}</span>
          </button>
        )}
      </div>

      {/* Mobile: one package at a time */}
      <div className="md:hidden mb-6">
        <MobilePackageSelector demandMode={demandMode} />
      </div>

      <div className="hidden md:block">
        <PortfolioPnLSummary portfolio={portfolio} demandMode={demandMode} onToggleDemandMode={toggleDemandMode} />
        <ComparisonTable packages={packages} portfolio={portfolio} demandMode={demandMode} />
        <CombinedMarketCapture demandMode={demandMode} />
      </div>
    </div>
  );
}
